/**
 * ВИЗУАЛЬНЫЕ ПРЕДПОЧТЕНИЯ — как должны выглядеть блоки, которые собирает AI.
 *
 * Этот файл можно править без разработчика. Всё, что здесь написано, дописывается в конец
 * инструкции ассистента — так же, как если бы вы объясняли дизайнеру, каким должен получаться
 * результат.
 *
 * ЧТО ЗДЕСЬ МОЖНО, А ЧТО НЕЛЬЗЯ
 *
 * Здесь место только вкусу: каким должен получиться результат. Правила, без которых ассистент
 * начнёт ошибаться — как звать инструменты, откуда брать id, чего нельзя выдумывать — живут
 * отдельно, в `ai-chat-drawer.prompt-core.ts`, и трогать их должен разработчик.
 *
 * Граница проведена так, что **этот файл можно очистить целиком**: ассистент продолжит работать
 * правильно, просто без предпочтений по внешнему виду. Если из-за правки здесь что-то ломается —
 * значит, строка попала не в тот файл, и её место в ядре.
 *
 * КАК ПРАВИТЬ
 *
 * 1. Одна мысль — одна строка. Обычными предложениями, как человеку.
 * 2. Строка, начинающаяся с решётки, — заголовок для вас: он помогает ориентироваться в файле и в
 *    инструкцию ассистента НЕ попадает. Пользуйтесь ими свободно.
 * 3. Пишите на английском: инструкция целиком на английском, а ответ пользователю ассистент всё
 *    равно даёт на языке вопроса — это правило стоит в ядре и от языка этого файла не зависит.
 * 4. Пустые строки и отступы не важны — они убираются при сборке.
 * 5. НЕ используйте символ обратной кавычки и сочетание «доллар с фигурной скобкой»: первое
 *    оборвёт текст, второе подставит в инструкцию что-то постороннее. Всё остальное — включая
 *    кавычки, дефисы и знаки препинания — писать можно свободно.
 * 6. Каждая строка реально меняет поведение модели. Добавили — проверьте на живой странице, что
 *    получилось то, что задумано. Меньше строк, но точнее, работает лучше, чем длинный список:
 *    модель следует инструкциям буквально, и десять уточнений про отступы сделают её осторожной
 *    там, где вы этого не просили.
 *
 * ЧТО СЮДА ОБЫЧНО ДОБАВЛЯЮТ
 *
 * Характер оформления («строгий корпоративный вид», «яркий и контрастный»), плотность вёрстки,
 * длину текстов, которые пишет ассистент, выравнивание по умолчанию, сколько кнопок уместно в
 * одном блоке.
 *
 * Чего добавлять не стоит: названий инструментов, имён полей и любых указаний про id — это ядро.
 * Спека рядом с этим файлом на такие слова ругается намеренно.
 *
 * ---
 *
 * Первая содержательная версия — 2026-08-28. До неё здесь была одна строка (первая в списке),
 * перенесённая из общего промта при разделении. Остальное дописано и **живьём не проверялось**:
 * это отправная точка для настройки, а не выверенный результат.
 */
export const AI_CHAT_DRAWER_STYLE_GUIDE = `
# Общий характер
Blocks you create should look light and modern, in line with current marketing design.
When two options look equally reasonable, choose the plainer one — a page that looks simple reads better than one that looks busy.

# Ритм и воздух
Give blocks generous vertical breathing room; a cramped page reads as unfinished.
Let neighbouring blocks differ in background so the page reads as distinct sections, rather than as one long sheet.

# Одна мысль в блоке
Each block should carry a single message: a heading, a short supporting paragraph, and at most one call to action.
Keep headings short — six to eight words is usually enough.
Keep supporting text to two or three sentences: a landing page is scanned, not read.

# Цвет
Keep the number of accent colours on one screen small — one accent doing one job reads as deliberate, four read as an accident.
Never place text on a background it barely contrasts with.

# Кнопки
At most one primary button per block; when a second action belongs there, give it the secondary style.
Button text should name the action — "Start free trial" rather than "Click here".

# Картинки
Add an image when it shows something real about the subject, and leave it out when it would only be decoration.

# Сдержанность
When the user asks for a block, fill it with what that block needs and nothing more.
`;

/**
 * Разбирает файл выше в строки инструкции: заголовки (строки с решётки), пустые строки и отступы
 * убираются, остальное остаётся как есть.
 *
 * Пустой файл даёт пустой список — и это рабочее состояние, а не поломка: смысл в том, что
 * визуальные предпочтения можно снять целиком, не трогая ядро.
 */
export function styleGuideLines(guide: string = AI_CHAT_DRAWER_STYLE_GUIDE): string[] {
    return guide
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0 && !line.startsWith('#'));
}
